/**
 * Haptic Feedback Utility
 * 
 * Provides tactile feedback for important user interactions.
 * Uses the Vibration API with graceful fallback for unsupported devices.
 * 
 * Designed for seniors: gentle, confirming vibrations that don't startle.
 */

// Check if vibration is supported
const isVibrationSupported = (): boolean => {
  return 'vibrate' in navigator && typeof navigator.vibrate === 'function';
};

// Vibration patterns (in milliseconds)
// Format: [vibrate, pause, vibrate, pause, ...]
const PATTERNS = {
  // Light tap - subtle confirmation for button presses
  tap: [10],
  
  // Soft tap - even lighter for minor interactions
  softTap: [5],
  
  // Double tap - for toggle actions
  doubleTap: [10, 50, 10],
  
  // Success - pleasant confirmation pattern
  success: [10, 30, 10, 30, 20],
  
  // Warning - attention-getting but not alarming
  warning: [20, 50, 30],
  
  // Error - distinct pattern that signals something went wrong
  error: [30, 50, 30, 50, 40],
  
  // Notification - gentle alert
  notification: [15, 100, 15],
  
  // Reminder - friendly nudge
  reminder: [10, 80, 10, 80, 15],
  
  // Selection - subtle feedback for item selection
  selection: [8],
  
  // Heavy - for important confirmations like delete
  heavy: [40],
  
  // Long press - for drag start or context menu
  longPress: [15, 30, 8],
} as const;

type HapticPattern = keyof typeof PATTERNS;

// User preference storage
const HAPTIC_ENABLED_KEY = 'dipps_haptics_enabled';
const HAPTIC_INTENSITY_KEY = 'dipps_haptics_intensity';

type HapticIntensity = 'off' | 'light' | 'medium' | 'strong';

// Get user's haptic preference
export const getHapticEnabled = (): boolean => {
  try {
    const stored = localStorage.getItem(HAPTIC_ENABLED_KEY);
    // Default to true if not set (most seniors benefit from haptic feedback)
    return stored === null ? true : stored === 'true';
  } catch {
    return true;
  }
};

// Set user's haptic preference
export const setHapticEnabled = (enabled: boolean): void => {
  try {
    localStorage.setItem(HAPTIC_ENABLED_KEY, String(enabled));
  } catch {
    // Silently fail if localStorage is unavailable
  }
};

// Get haptic intensity preference
export const getHapticIntensity = (): HapticIntensity => {
  try {
    const stored = localStorage.getItem(HAPTIC_INTENSITY_KEY);
    if (stored && ['off', 'light', 'medium', 'strong'].includes(stored)) {
      return stored as HapticIntensity;
    }
    return 'medium'; // Default
  } catch {
    return 'medium';
  }
};

// Set haptic intensity preference
export const setHapticIntensity = (intensity: HapticIntensity): void => {
  try {
    localStorage.setItem(HAPTIC_INTENSITY_KEY, intensity);
    if (intensity === 'off') {
      setHapticEnabled(false);
    } else if (!getHapticEnabled()) {
      setHapticEnabled(true);
    }
  } catch {
    // Silently fail
  }
};

// Intensity multipliers
const intensityMultipliers: Record<HapticIntensity, number> = {
  off: 0,
  light: 0.5,
  medium: 1,
  strong: 1.5,
};

// Scale pattern by intensity
const scalePattern = (pattern: number[], intensity: HapticIntensity): number[] => {
  const multiplier = intensityMultipliers[intensity];
  if (multiplier === 0) return [];
  
  return pattern.map((duration, index) => {
    // Only scale vibration durations (even indices), keep pauses as-is
    if (index % 2 === 0) {
      return Math.round(duration * multiplier);
    }
    return duration;
  });
};

/**
 * Trigger haptic feedback with a specific pattern
 */
export const haptic = (pattern: HapticPattern = 'tap'): boolean => {
  // Check if haptics are enabled
  if (!getHapticEnabled()) return false;
  
  // Check if device supports vibration
  if (!isVibrationSupported()) return false;
  
  // Get intensity and scale pattern
  const intensity = getHapticIntensity();
  if (intensity === 'off') return false;
  
  const basePattern = PATTERNS[pattern];
  const scaledPattern = scalePattern([...basePattern], intensity);
  
  if (scaledPattern.length === 0) return false;
  
  try {
    return navigator.vibrate(scaledPattern);
  } catch {
    return false;
  }
};

/**
 * Stop any ongoing vibration
 */
export const stopHaptic = (): boolean => {
  if (!isVibrationSupported()) return false;
  
  try {
    return navigator.vibrate(0);
  } catch {
    return false;
  }
};

// Convenience functions for common patterns
export const hapticTap = () => haptic('tap');
export const hapticSoftTap = () => haptic('softTap');
export const hapticDoubleTap = () => haptic('doubleTap');
export const hapticSuccess = () => haptic('success');
export const hapticWarning = () => haptic('warning');
export const hapticError = () => haptic('error');
export const hapticNotification = () => haptic('notification');
export const hapticReminder = () => haptic('reminder');
export const hapticSelection = () => haptic('selection');
export const hapticHeavy = () => haptic('heavy');
export const hapticLongPress = () => haptic('longPress');

/**
 * Custom vibration pattern
 */
export const hapticCustom = (pattern: number[]): boolean => {
  if (!getHapticEnabled()) return false;
  if (!isVibrationSupported()) return false;
  
  const intensity = getHapticIntensity();
  if (intensity === 'off') return false;
  
  const scaledPattern = scalePattern(pattern, intensity);
  if (scaledPattern.length === 0) return false;
  
  try {
    return navigator.vibrate(scaledPattern);
  } catch {
    return false;
  }
};

/**
 * Check if haptic feedback is available on this device
 */
export const isHapticAvailable = (): boolean => {
  return isVibrationSupported();
};

/**
 * React hook for haptic feedback
 */
export const useHaptics = () => {
  return {
    isAvailable: isHapticAvailable(),
    enabled: getHapticEnabled(),
    intensity: getHapticIntensity(),
    setEnabled: setHapticEnabled,
    setIntensity: setHapticIntensity,
    tap: hapticTap,
    softTap: hapticSoftTap,
    doubleTap: hapticDoubleTap,
    success: hapticSuccess,
    warning: hapticWarning,
    error: hapticError,
    notification: hapticNotification,
    reminder: hapticReminder,
    selection: hapticSelection,
    heavy: hapticHeavy,
    longPress: hapticLongPress,
    custom: hapticCustom,
    stop: stopHaptic,
  };
};

// Export types
export type { HapticPattern, HapticIntensity };
